import { useCallback, useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

// Spotify Web Playback SDK global
declare global {
  interface Window {
    Spotify?: any;
    onSpotifyWebPlaybackSDKReady?: () => void;
  }
}

const SDK_SRC = "https://sdk.scdn.co/spotify-player.js";

export interface SpotifyConnection {
  access_token: string;
  expires_at: string;
  is_premium: boolean;
  spotify_user_id: string | null;
}

export interface PlaybackState {
  track_name?: string;
  artist_name?: string;
  album_art?: string;
  paused: boolean;
  duration_ms: number;
  position_ms: number;
}

export interface SpotifyPlaylist {
  id: string;
  name: string;
  image?: string;
  uri: string;
}

let sdkLoadPromise: Promise<void> | null = null;
function loadSdk(): Promise<void> {
  if (window.Spotify) return Promise.resolve();
  if (sdkLoadPromise) return sdkLoadPromise;
  sdkLoadPromise = new Promise((resolve) => {
    window.onSpotifyWebPlaybackSDKReady = () => resolve();
    const s = document.createElement("script");
    s.src = SDK_SRC;
    s.async = true;
    document.body.appendChild(s);
  });
  return sdkLoadPromise;
}

export function useSpotifyPlayer() {
  const [connection, setConnection] = useState<SpotifyConnection | null>(null);
  const [loading, setLoading] = useState(true);
  const [ready, setReady] = useState(false);
  const [deviceId, setDeviceId] = useState<string | null>(null);
  const [state, setState] = useState<PlaybackState | null>(null);
  const [playlists, setPlaylists] = useState<SpotifyPlaylist[]>([]);
  const [error, setError] = useState<string | null>(null);
  const playerRef = useRef<any>(null);
  const tokenRef = useRef<string | null>(null);

  // Refresh helper: also updates local state
  const refreshToken = useCallback(async (): Promise<string | null> => {
    const { data, error: fnErr } = await supabase.functions.invoke("spotify-refresh-token");
    if (fnErr || !data?.access_token) {
      setError(fnErr?.message ?? "Failed to refresh Spotify token");
      return null;
    }
    tokenRef.current = data.access_token;
    setConnection((c) =>
      c ? { ...c, access_token: data.access_token, expires_at: data.expires_at } : c
    );
    return data.access_token;
  }, []);

  const getFreshToken = useCallback(async (): Promise<string | null> => {
    if (!connection) return null;
    const expiresAt = new Date(connection.expires_at).getTime();
    if (Date.now() < expiresAt - 30_000 && tokenRef.current) {
      return tokenRef.current;
    }
    return await refreshToken();
  }, [connection, refreshToken]);

  // Load stored connection on mount
  useEffect(() => {
    (async () => {
      setLoading(true);
      const { data: sess } = await supabase.auth.getSession();
      if (!sess.session) {
        setLoading(false);
        return;
      }
      const { data, error: qErr } = await supabase
        .from("spotify_connections")
        .select("access_token, expires_at, is_premium, spotify_user_id")
        .maybeSingle();
      if (qErr) {
        setError(qErr.message);
        setLoading(false);
        return;
      }
      if (data) {
        setConnection(data as SpotifyConnection);
        tokenRef.current = data.access_token;
        // If expired, refresh immediately
        if (new Date(data.expires_at).getTime() < Date.now() + 30_000) {
          await refreshToken();
        }
      }
      setLoading(false);
    })();
  }, [refreshToken]);

  // Initialize the Web Playback SDK player once we have a Premium connection
  useEffect(() => {
    if (!connection || !connection.is_premium) return;
    let cancelled = false;

    (async () => {
      await loadSdk();
      if (cancelled) return;
      if (playerRef.current) return;

      const player = new window.Spotify.Player({
        name: "MindMail",
        getOAuthToken: async (cb: (t: string) => void) => {
          const t = await getFreshToken();
          if (t) cb(t);
        },
        volume: 0.5,
      });
      playerRef.current = player;

      player.addListener("ready", ({ device_id }: any) => {
        setDeviceId(device_id);
        setReady(true);
      });
      player.addListener("not_ready", () => setReady(false));
      player.addListener("player_state_changed", (s: any) => {
        if (!s) return;
        const track = s.track_window?.current_track;
        setState({
          track_name: track?.name,
          artist_name: track?.artists?.map((a: any) => a.name).join(", "),
          album_art: track?.album?.images?.[0]?.url,
          paused: s.paused,
          duration_ms: s.duration,
          position_ms: s.position,
        });
      });
      player.addListener("initialization_error", ({ message }: any) => setError(message));
      player.addListener("authentication_error", ({ message }: any) => setError(message));
      player.addListener("account_error", ({ message }: any) => setError(message));

      await player.connect();
    })();

    return () => {
      cancelled = true;
    };
  }, [connection, getFreshToken]);

  // Fetch playlists once connected
  useEffect(() => {
    if (!connection) return;
    (async () => {
      const token = await getFreshToken();
      if (!token) return;
      const res = await fetch("https://api.spotify.com/v1/me/playlists?limit=50", {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return;
      const json = await res.json();
      setPlaylists(
        (json.items ?? []).map((p: any) => ({
          id: p.id,
          name: p.name,
          image: p.images?.[0]?.url,
          uri: p.uri,
        }))
      );
    })();
  }, [connection, getFreshToken]);

  const callApi = useCallback(
    async (path: string, init: RequestInit = {}) => {
      const token = await getFreshToken();
      if (!token) return;
      await fetch(`https://api.spotify.com/v1${path}`, {
        ...init,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          ...(init.headers ?? {}),
        },
      });
    },
    [getFreshToken]
  );

  const play = useCallback(async () => playerRef.current?.resume(), []);
  const pause = useCallback(async () => playerRef.current?.pause(), []);
  const next = useCallback(async () => playerRef.current?.nextTrack(), []);
  const prev = useCallback(async () => playerRef.current?.previousTrack(), []);
  const setVolume = useCallback(async (v: number) => playerRef.current?.setVolume(v), []);

  const transferHere = useCallback(async () => {
    if (!deviceId) return;
    await callApi("/me/player", {
      method: "PUT",
      body: JSON.stringify({ device_ids: [deviceId], play: false }),
    });
  }, [deviceId, callApi]);

  const playPlaylist = useCallback(
    async (uri: string) => {
      if (!deviceId) return;
      await callApi(`/me/player/play?device_id=${deviceId}`, {
        method: "PUT",
        body: JSON.stringify({ context_uri: uri }),
      });
    },
    [deviceId, callApi]
  );

  const disconnect = useCallback(async () => {
    try {
      playerRef.current?.disconnect();
    } catch {}
    playerRef.current = null;
    tokenRef.current = null;
    const { data: sess } = await supabase.auth.getSession();
    if (sess.session) {
      await supabase.from("spotify_connections").delete().eq("user_id", sess.session.user.id);
    }
    setConnection(null);
    setState(null);
    setDeviceId(null);
    setReady(false);
    setPlaylists([]);
  }, []);

  return {
    connection,
    loading,
    ready,
    deviceId,
    state,
    playlists,
    error,
    play,
    pause,
    next,
    prev,
    setVolume,
    transferHere,
    playPlaylist,
    disconnect,
  };
}
